import { useEffect, useState, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Mic, MicOff, Volume2, Sparkles, RefreshCw, Trophy, BookOpen, Target } from "lucide-react";
import lessons from "../data/lessons.json";
import { useSpeechRecognition, useSpeechSynthesis, scorePronunciation } from "../hooks/useSpeech";
import { startActivityTracking, stopActivityTracking } from "../lib/dailyPlan";
import { earnXP } from "../gamification";
import PremiumGate, { isLessonFree, isPremium } from "../components/PremiumGate";
import { getSmartResponse, resetContext } from "../conversationEngine";

// 5 oral steps: 0=intro, 1=listen-repeat, 2=read-words, 3=your-sentence, 4=dialogue, 5=complete
const STEPS = [
  { key: "intro", title: "Welcome", icon: "👋" },
  { key: "listen-repeat", title: "Listen & Repeat", icon: "👂" },
  { key: "read-words", title: "Pronounce Key Words", icon: "🔤" },
  { key: "your-sentence", title: "Your Own Sentence", icon: "💭" },
  { key: "dialogue", title: "Conversation", icon: "🗣️" },
  { key: "complete", title: "Done", icon: "🏆" },
];

export default function OralLesson() {
  const { day } = useParams();
  const navigate = useNavigate();
  const lesson = lessons.find((l) => l.day === Number(day));
  const [step, setStep] = useState(0);
  const [scores, setScores] = useState({}); // step → score
  const [chatMessages, setChatMessages] = useState([]);
  const chatEndRef = useRef(null);

  const { transcript, interim, listening, error, start, stop, reset, supported: micSupported } =
    useSpeechRecognition({ lang: "en-US", continuous: false });
  const { speak, cancel, speaking } = useSpeechSynthesis();

  // Track time toward daily 1hr ORAL target
  useEffect(() => {
    startActivityTracking("oral", "oral-lesson");
    return () => stopActivityTracking();
  }, []);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatMessages]);

  if (!lesson) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center text-center">
        <div>
          <div className="text-5xl mb-4">📭</div>
          <h2 className="text-2xl font-black text-gray-800 dark:text-ink-900">Lesson not found</h2>
          <Link to="/dashboard" className="text-gold-700 mt-4 inline-block font-bold">← Back to dashboard</Link>
        </div>
      </div>
    );
  }

  if (!isLessonFree(Number(day)) && !isPremium()) {
    return <PremiumGate feature={`Oral Lesson ${day}: ${lesson.topic}`} />;
  }

  const totalSteps = STEPS.length;
  const progressPct = ((step + 1) / totalSteps) * 100;

  function next() {
    cancel();
    stop();
    reset();
    setStep((s) => Math.min(s + 1, totalSteps - 1));
  }
  function prev() {
    cancel();
    stop();
    reset();
    setStep((s) => Math.max(s - 1, 0));
  }
  function recordScore(stepIdx, score) {
    setScores((p) => ({ ...p, [stepIdx]: Math.max(p[stepIdx] || 0, score) }));
    earnXP(score >= 80 ? 15 : score >= 50 ? 10 : 5, "oral");
  }

  return (
    <div className="page-enter full-bleed -mt-6 min-h-[calc(100vh-4rem)] flex flex-col bg-paper text-ink-900">
      {/* Top bar */}
      <header className="p-4 flex items-center gap-3">
        <Link to={`/lesson/${day}`} className="p-1.5 hover:bg-paper-deep rounded-lg transition-colors">
          <ArrowLeft size={20} />
        </Link>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full bg-sage-light/30 border border-sage-light/40">
              🌙 Evening Oral
            </span>
            <span className="text-xs text-ink-500">Day {day}</span>
          </div>
          <p className="text-xs text-ink-600 truncate">{lesson.topic}</p>
        </div>
        <span className="text-sm font-bold text-ink-500">{step + 1}/{totalSteps}</span>
      </header>

      <div className="px-4">
        <div className="h-2 bg-paper-deep rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-sage via-sage-light to-sky-dark transition-all duration-700"
            style={{ width: `${progressPct}%` }}
          />
        </div>
      </div>

      {/* Step indicator */}
      <div className="px-4 mt-3 flex justify-center">
        <p className="text-xs text-ink-500 font-bold uppercase tracking-wider">
          {STEPS[step].icon} Step {step + 1} · {STEPS[step].title}
        </p>
      </div>

      {/* Step content */}
      <main className="flex-1 px-4 sm:px-6 lg:px-8 py-6 max-w-3xl mx-auto w-full">
        {step === 0 && (
          <IntroStep lesson={lesson} onNext={next} />
        )}
        {step === 1 && (
          <ListenRepeatStep
            lesson={lesson}
            speak={speak}
            speaking={speaking}
            transcript={transcript}
            interim={interim}
            listening={listening}
            error={error}
            start={start}
            stop={stop}
            reset={reset}
            micSupported={micSupported}
            onScore={(s) => recordScore(1, s)}
            onNext={next}
            currentScore={scores[1]}
          />
        )}
        {step === 2 && (
          <ReadWordsStep
            lesson={lesson}
            speak={speak}
            speaking={speaking}
            transcript={transcript}
            listening={listening}
            interim={interim}
            start={start}
            stop={stop}
            reset={reset}
            micSupported={micSupported}
            onScore={(s) => recordScore(2, s)}
            onNext={next}
            currentScore={scores[2]}
          />
        )}
        {step === 3 && (
          <YourSentenceStep
            lesson={lesson}
            transcript={transcript}
            interim={interim}
            listening={listening}
            start={start}
            stop={stop}
            reset={reset}
            speak={speak}
            micSupported={micSupported}
            onScore={(s) => recordScore(3, s)}
            onNext={next}
            currentScore={scores[3]}
          />
        )}
        {step === 4 && (
          <DialogueStep
            lesson={lesson}
            chatMessages={chatMessages}
            setChatMessages={setChatMessages}
            chatEndRef={chatEndRef}
            transcript={transcript}
            interim={interim}
            listening={listening}
            start={start}
            stop={stop}
            reset={reset}
            speak={speak}
            speaking={speaking}
            cancel={cancel}
            micSupported={micSupported}
            onNext={() => { recordScore(4, 80); next(); }}
          />
        )}
        {step === 5 && (
          <CompleteStep lesson={lesson} scores={scores} day={day} navigate={navigate} />
        )}
      </main>

      {/* Footer nav */}
      {step > 0 && step < totalSteps - 1 && (
        <footer className="sticky bottom-0 backdrop-blur-md bg-black/30 border-t border-cream-200 px-4 py-3">
          <div className="max-w-3xl mx-auto flex items-center justify-between">
            <button onClick={prev} className="text-sm text-ink-600 hover:text-ink-900 px-3 py-2 rounded-lg hover:bg-paper-deep transition flex items-center gap-1">
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
            <button onClick={next} className="text-sm font-bold bg-white text-sage-dark px-5 py-2 rounded-full hover:scale-105 transition shadow-lg flex items-center gap-1.5">
              Next <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </footer>
      )}
    </div>
  );
}

// ============================================================
// STEP 0 — INTRO
// ============================================================
function IntroStep({ lesson, onNext }) {
  return (
    <div className="text-center space-y-6 py-8">
      <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-sage-light/20 border border-sage-light/40 text-xs font-bold uppercase tracking-wider">
        <Mic className="w-3.5 h-3.5" /> Speaking Practice
      </div>
      <h1 className="text-3xl md:text-4xl font-black">{lesson.topic}</h1>
      <p className="text-ink-600 text-base max-w-md mx-auto">
        You learned this topic in writing this morning. Now let's <strong className="text-sage-light">SPEAK</strong> the same words and sentences out loud.
      </p>

      <div className="bg-paper-deep backdrop-blur-xl border border-cream-300 rounded-3xl p-6 max-w-md mx-auto">
        <p className="text-xs text-ink-500 uppercase tracking-wider font-bold mb-2">Today's sentence</p>
        <p className="text-2xl font-black mb-4">"{lesson.sentence}"</p>
        <div className="flex flex-wrap gap-2 justify-center">
          {lesson.words?.slice(0, 5).map((w, i) => (
            <span key={i} className="text-xs px-2.5 py-1 rounded-full bg-paper-deep text-ink-700 font-semibold">
              {w.word}
            </span>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 max-w-md mx-auto text-left">
        <div className="bg-paper-deep border border-cream-200 rounded-2xl p-3">
          <Target className="w-5 h-5 text-sage-light mb-1" />
          <p className="text-xs font-bold">5 Steps</p>
          <p className="text-[10px] text-ink-500">Listen → Repeat → Build → Talk</p>
        </div>
        <div className="bg-paper-deep border border-cream-200 rounded-2xl p-3">
          <Sparkles className="w-5 h-5 text-gold-300 mb-1" />
          <p className="text-xs font-bold">+50 XP</p>
          <p className="text-[10px] text-ink-500">Counts toward 1hr oral target</p>
        </div>
      </div>

      <button
        onClick={onNext}
        className="bg-white text-sage-dark font-black px-8 py-4 rounded-full text-base shadow-2xl hover:scale-105 transition-all inline-flex items-center gap-2"
      >
        🎤 Start speaking <ArrowRight className="w-5 h-5" />
      </button>
    </div>
  );
}

// ============================================================
// STEP 1 — LISTEN & REPEAT
// ============================================================
function ListenRepeatStep({ lesson, speak, speaking, transcript, interim, listening, error, start, stop, reset, micSupported, onScore, currentScore, onNext }) {
  const [hasScored, setHasScored] = useState(false);

  useEffect(() => {
    if (!listening && transcript && !hasScored) {
      const result = scorePronunciation(lesson.sentence, transcript);
      onScore(result.score);
      setHasScored(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listening, transcript]);

  function tryAgain() {
    setHasScored(false);
    reset();
  }

  const result = transcript ? scorePronunciation(lesson.sentence, transcript) : null;

  return (
    <div className="space-y-5">
      <h2 className="text-xl font-black text-center">👂 Listen, then repeat the sentence</h2>

      {/* Sentence card */}
      <div className="bg-paper-deep backdrop-blur-xl border border-cream-300 rounded-3xl p-6 text-center">
        <p className="text-2xl md:text-3xl font-black mb-4">"{lesson.sentence}"</p>
        <button
          onClick={() => speak(lesson.sentence, { lang: "en-US", rate: 0.85 })}
          disabled={speaking}
          className="inline-flex items-center gap-2 bg-sage hover:bg-sage-light text-ink-900 font-bold px-5 py-2.5 rounded-full transition-all hover:scale-105 disabled:opacity-50"
        >
          <Volume2 className="w-4 h-4" />
          {speaking ? "Playing…" : "Listen again"}
        </button>
      </div>

      {/* Mic */}
      <div className="text-center">
        <p className="text-sm text-ink-600 mb-3">Now you say it →</p>
        <button
          onClick={() => (listening ? stop() : start())}
          disabled={!micSupported || speaking}
          className={`relative w-20 h-20 rounded-full shadow-2xl mx-auto transition-all ${
            listening
              ? "bg-gradient-to-br from-berry-light0 to-rust-dark scale-110 animate-pulse"
              : "bg-gradient-to-br from-sage to-sage-dark hover:scale-105"
          } disabled:opacity-40 disabled:cursor-not-allowed`}
        >
          {listening && <div className="absolute inset-0 rounded-full bg-berry/40 animate-ping" />}
          {listening ? <MicOff className="w-9 h-9 text-ink-900 mx-auto" /> : <Mic className="w-9 h-9 text-ink-900 mx-auto" />}
        </button>
        <p className="text-xs text-ink-500 mt-3">
          {listening ? "🎙️ Listening — speak now" : interim ? interim : "Tap mic and say the sentence"}
        </p>
        {error && <p className="text-xs text-berry-light mt-2">{error}</p>}
      </div>

      {/* Result */}
      {result && hasScored && (
        <div className={`rounded-2xl p-5 border-2 ${
          result.score >= 80 ? "bg-sage/20 border-sage" :
          result.score >= 50 ? "bg-gold-500/20 border-gold-500" :
          "bg-rust/20 border-rust-light"
        }`}>
          <div className="flex items-center justify-between mb-3">
            <p className="font-black text-lg">{result.score >= 80 ? "🌟 Excellent!" : result.score >= 50 ? "👍 Good try!" : "💪 Keep trying!"}</p>
            <p className="text-3xl font-black">{result.score}%</p>
          </div>
          <p className="text-xs text-ink-700 mb-2">You said: <span className="italic">"{transcript}"</span></p>
          <p className="text-xs text-ink-700 mb-3">Matched <strong>{result.matched}/{result.total}</strong> words</p>
          {result.missingWords?.length > 0 && (
            <p className="text-xs text-ink-600 mb-3">
              Missed: {result.missingWords.map((w, i) => <span key={i} className="inline-block bg-paper-deep px-2 py-0.5 rounded mr-1 font-mono">{w}</span>)}
            </p>
          )}
          <div className="flex gap-2 mt-3">
            <button onClick={tryAgain} className="flex-1 bg-cream-200 text-ink-900 font-bold py-2 rounded-full text-sm flex items-center justify-center gap-1 hover:bg-white/30">
              <RefreshCw className="w-3.5 h-3.5" /> Try again
            </button>
            <button onClick={onNext} className="flex-1 bg-white text-sage-dark font-bold py-2 rounded-full text-sm flex items-center justify-center gap-1 hover:scale-105">
              Next <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// STEP 2 — READ KEY WORDS
// ============================================================
function ReadWordsStep({ lesson, speak, speaking, transcript, interim, listening, start, stop, reset, micSupported, onScore, currentScore, onNext }) {
  const [wordIdx, setWordIdx] = useState(0);
  const [scores, setScores] = useState({});
  // Use lesson.words OR fall back to lesson.examples split into individual words for practice/light lessons
  const wordsRaw = (lesson.words || []).slice(0, 6);
  const fallbackWords = (lesson.examples || []).slice(0, 6).map((ex) => {
    const text = typeof ex === "string" ? ex : ex;
    return { word: text, role: "Example", meaning: "Example sentence", why: "Practice this phrase out loud." };
  });
  const words = wordsRaw.length > 0 ? wordsRaw : fallbackWords;
  const current = words[wordIdx];

  useEffect(() => {
    if (!listening && transcript && current) {
      const result = scorePronunciation(current.word, transcript);
      setScores((p) => ({ ...p, [wordIdx]: result.score }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listening, transcript]);

  // If we have no words AND no examples, auto-skip to next step
  useEffect(() => {
    if (words.length === 0) {
      onScore(80); // give a default score so progress doesn't show as 0
      onNext();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function nextWord() {
    reset();
    if (wordIdx < words.length - 1) setWordIdx(wordIdx + 1);
    else {
      const avg = Object.values(scores).reduce((a, b) => a + b, 0) / Math.max(Object.keys(scores).length, 1);
      onScore(Math.round(avg));
      onNext();
    }
  }

  if (!current) {
    return (
      <div className="text-center py-8 space-y-3">
        <p className="text-sm text-ink-600">No vocabulary practice for this day — skipping to next step.</p>
        <button onClick={onNext} className="bg-white text-sage-dark font-bold px-5 py-2 rounded-full text-sm hover:scale-105 transition">Continue →</button>
      </div>
    );
  }

  const myScore = scores[wordIdx];

  return (
    <div className="space-y-5">
      <h2 className="text-xl font-black text-center">🔤 Pronounce each key word</h2>
      <p className="text-center text-xs text-ink-500">Word {wordIdx + 1} of {words.length}</p>

      <div className="bg-paper-deep backdrop-blur-xl border border-cream-300 rounded-3xl p-8 text-center">
        <p className="text-4xl md:text-5xl font-black mb-3 text-gold-300">{current.word}</p>
        <p className="text-sm text-ink-600 mb-1">{current.role}</p>
        <p className="text-xs text-ink-500 italic mb-4">{current.meaning}</p>
        <button
          onClick={() => speak(current.word, { lang: "en-US", rate: 0.7 })}
          disabled={speaking}
          className="inline-flex items-center gap-2 bg-sage hover:bg-sage-light text-ink-900 font-bold px-4 py-2 rounded-full text-sm hover:scale-105 transition disabled:opacity-50"
        >
          <Volume2 className="w-4 h-4" /> Hear it
        </button>
      </div>

      <div className="text-center">
        <button
          onClick={() => (listening ? stop() : start())}
          disabled={!micSupported || speaking}
          className={`relative w-16 h-16 rounded-full shadow-xl mx-auto transition-all ${
            listening
              ? "bg-berry scale-110 animate-pulse"
              : "bg-sage hover:scale-105"
          } disabled:opacity-40`}
        >
          {listening && <div className="absolute inset-0 rounded-full bg-berry/40 animate-ping" />}
          {listening ? <MicOff className="w-7 h-7 text-ink-900 mx-auto" /> : <Mic className="w-7 h-7 text-ink-900 mx-auto" />}
        </button>
        <p className="text-xs text-ink-500 mt-2">{listening ? "🎙️ Speak the word" : interim || "Tap mic and say the word"}</p>
      </div>

      {myScore !== undefined && (
        <div className={`rounded-2xl p-4 text-center border-2 ${myScore >= 80 ? "bg-sage/20 border-sage" : myScore >= 50 ? "bg-gold-500/20 border-gold-500" : "bg-rust/20 border-rust-light"}`}>
          <p className="text-2xl font-black">{myScore}%</p>
          <p className="text-xs text-ink-600 italic mt-1">"{transcript}"</p>
          <button onClick={nextWord} className="mt-3 bg-white text-sage-dark font-bold px-5 py-2 rounded-full text-sm hover:scale-105 transition flex items-center gap-1 mx-auto">
            {wordIdx < words.length - 1 ? "Next word" : "Continue"} <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* Word progress dots */}
      <div className="flex justify-center gap-1.5">
        {words.map((_, i) => (
          <div key={i} className={`w-2 h-2 rounded-full transition ${
            scores[i] !== undefined ? "bg-sage-light" : i === wordIdx ? "bg-white" : "bg-cream-200"
          }`} />
        ))}
      </div>
    </div>
  );
}

// ============================================================
// STEP 3 — YOUR OWN SENTENCE
// ============================================================
function YourSentenceStep({ lesson, transcript, interim, listening, start, stop, reset, speak, micSupported, onScore, currentScore, onNext }) {
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!listening && transcript && !submitted) {
      // Score by checking if user used at least 50% of key words/concepts from the lesson
      const keyTerms = (lesson.words || []).map((w) => w.word).slice(0, 4);
      const lower = transcript.toLowerCase();
      const matched = keyTerms.filter((t) => lower.includes(t.toLowerCase())).length;
      const score = Math.round((matched / Math.max(keyTerms.length, 1)) * 100);
      // Bonus: word count > 3 (full sentence)
      const finalScore = Math.min(100, score + (transcript.split(/\s+/).length >= 4 ? 30 : 0));
      onScore(finalScore);
      setSubmitted(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listening, transcript]);

  function tryAgain() {
    setSubmitted(false);
    reset();
  }

  return (
    <div className="space-y-5">
      <h2 className="text-xl font-black text-center">💭 Make your own sentence</h2>

      <div className="bg-paper-deep backdrop-blur-xl border border-cream-300 rounded-3xl p-5">
        <p className="text-xs text-ink-500 uppercase tracking-wider font-bold mb-2">Your task</p>
        <p className="text-base font-bold mb-3">{lesson.practice?.prompt || "Make a sentence using today's grammar."}</p>
        <p className="text-sm text-ink-600 mb-1"><span className="font-bold text-gold-300">Example:</span> "{lesson.practice?.example || lesson.sentence}"</p>
        {lesson.practice?.hint && (
          <p className="text-xs text-ink-500 italic mt-2">💡 {lesson.practice.hint}</p>
        )}
      </div>

      {/* Mic */}
      <div className="text-center">
        <button
          onClick={() => (listening ? stop() : start())}
          disabled={!micSupported}
          className={`relative w-20 h-20 rounded-full shadow-2xl mx-auto transition-all ${
            listening
              ? "bg-gradient-to-br from-berry-light0 to-rust-dark scale-110 animate-pulse"
              : "bg-gradient-to-br from-sage to-sage-dark hover:scale-105"
          } disabled:opacity-40`}
        >
          {listening && <div className="absolute inset-0 rounded-full bg-berry/40 animate-ping" />}
          {listening ? <MicOff className="w-9 h-9 text-ink-900 mx-auto" /> : <Mic className="w-9 h-9 text-ink-900 mx-auto" />}
        </button>
        <p className="text-sm text-ink-600 mt-3">{listening ? "🎙️ Listening — say your sentence" : interim || "Tap mic and create your sentence"}</p>
      </div>

      {submitted && transcript && (
        <div className="bg-paper-deep rounded-2xl p-5 border border-cream-300">
          <p className="text-xs text-ink-500 uppercase tracking-wider font-bold mb-2">You said</p>
          <p className="text-lg font-bold mb-3">"{transcript}"</p>
          <button onClick={() => speak(transcript, { lang: "en-US", rate: 0.9 })} className="inline-flex items-center gap-1 text-xs text-sage-light hover:underline">
            <Volume2 className="w-3 h-3" /> Hear it back
          </button>
          {currentScore !== undefined && (
            <p className="text-2xl font-black mt-3">{currentScore}% on track!</p>
          )}
          <div className="flex gap-2 mt-4">
            <button onClick={tryAgain} className="flex-1 bg-cream-200 font-bold py-2 rounded-full text-sm flex items-center justify-center gap-1 hover:bg-white/30">
              <RefreshCw className="w-3.5 h-3.5" /> Try again
            </button>
            <button onClick={onNext} className="flex-1 bg-white text-sage-dark font-bold py-2 rounded-full text-sm flex items-center justify-center gap-1 hover:scale-105">
              Next <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}

      {/* Common mistakes hint */}
      {lesson.practice?.commonMistakes && lesson.practice.commonMistakes.length > 0 && !submitted && (
        <details className="bg-gold-500/10 border border-gold-500/30 rounded-xl p-4">
          <summary className="text-xs font-bold text-gold-300 cursor-pointer">⚠️ Common mistakes to avoid</summary>
          <div className="mt-2 space-y-2">
            {lesson.practice.commonMistakes.map((m, i) => (
              <div key={i} className="text-xs text-ink-700">
                <span className="line-through text-paper">{m.wrong}</span>
                {" → "}
                <span className="text-sage-light font-bold">{m.fix}</span>
                <p className="text-ink-500 italic mt-0.5">{m.tip}</p>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

// ============================================================
// STEP 4 — DIALOGUE
// ============================================================
function DialogueStep({ lesson, chatMessages, setChatMessages, chatEndRef, transcript, interim, listening, start, stop, reset, speak, speaking, cancel, micSupported, onNext }) {
  const initRef = useRef(false);
  const [exchangeCount, setExchangeCount] = useState(0);

  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;
    resetContext();
    const greeting = `Let's chat about "${lesson.topic}". For example: "${lesson.sentence}". Now you try — make a sentence about your own life using this grammar.`;
    setChatMessages([{ role: "ai", text: greeting }]);
    setTimeout(() => speak(greeting, { lang: "en-US", rate: 0.9 }), 500);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!listening && transcript && transcript.trim()) {
      handleUserMessage(transcript);
      reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listening, transcript]);

  function handleUserMessage(text) {
    setChatMessages((p) => [...p, { role: "user", text }]);
    setExchangeCount((c) => c + 1);
    setTimeout(() => {
      const resp = getSmartResponse("daily", text, exchangeCount + 1);
      const tipMatch = resp.match(/\n\n(💡 Tip:.+)$/);
      const aiText = tipMatch ? resp.replace(tipMatch[0], "") : resp;
      const correction = tipMatch ? tipMatch[1].replace("💡 Tip: ", "") : null;
      setChatMessages((p) => [...p, { role: "ai", text: aiText, correction }]);
      speak(aiText, { lang: "en-US", rate: 0.9 });
    }, 600);
  }

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-black text-center">🗣️ Have a conversation about today's topic</h2>

      <div className="bg-paper-deep rounded-2xl p-3 max-h-[40vh] overflow-y-auto space-y-3 border border-cream-200">
        {chatMessages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm ${
              m.role === "user" ? "bg-sage text-ink-900 rounded-tr-sm" : "bg-paper-deep text-ink-900 rounded-tl-sm"
            }`}>
              <p className="whitespace-pre-wrap">{m.text}</p>
              {m.correction && (
                <div className="mt-2 px-3 py-1.5 rounded-lg bg-gold-500/20 border border-gold-300/30 text-gold-300 text-xs">
                  💡 <strong>Tip:</strong> {m.correction}
                </div>
              )}
            </div>
          </div>
        ))}
        {listening && interim && (
          <div className="flex justify-end">
            <div className="max-w-[80%] px-4 py-2 rounded-2xl bg-sage-light/40 italic text-ink-800 text-sm">{interim}…</div>
          </div>
        )}
        <div ref={chatEndRef} />
      </div>

      <div className="text-center">
        <button
          onClick={() => { if (speaking) cancel(); listening ? stop() : start(); }}
          disabled={!micSupported}
          className={`relative w-16 h-16 rounded-full shadow-xl mx-auto transition ${
            listening ? "bg-berry scale-110 animate-pulse" : "bg-sage hover:scale-105"
          }`}
        >
          {listening && <div className="absolute inset-0 rounded-full bg-berry/40 animate-ping" />}
          {listening ? <MicOff className="w-7 h-7 text-ink-900 mx-auto" /> : <Mic className="w-7 h-7 text-ink-900 mx-auto" />}
        </button>
        <p className="text-xs text-ink-500 mt-2">
          {exchangeCount >= 3 ? `Great chat! ${exchangeCount} replies — tap "Done" when ready.` : `Reply ${exchangeCount}/3 — keep talking!`}
        </p>
      </div>

      {exchangeCount >= 2 && (
        <button onClick={onNext} className="w-full bg-white text-sage-dark font-black py-3 rounded-full text-sm hover:scale-[1.02] transition shadow-lg flex items-center justify-center gap-2">
          <Trophy className="w-4 h-4" /> Done — finish lesson
        </button>
      )}
    </div>
  );
}

// ============================================================
// STEP 5 — COMPLETE
// ============================================================
function CompleteStep({ lesson, scores, day, navigate }) {
  const allScores = Object.values(scores);
  const avg = allScores.length ? Math.round(allScores.reduce((a, b) => a + b, 0) / allScores.length) : 0;
  const nextDay = Number(day) + 1;

  return (
    <div className="text-center space-y-6 py-8">
      <div className="text-6xl mb-2 animate-bounce">🏆</div>
      <h1 className="text-3xl md:text-4xl font-black">Oral lesson complete!</h1>
      <p className="text-ink-600">You practiced "{lesson.topic}" out loud.</p>

      <div className="bg-paper-deep backdrop-blur-xl border border-cream-300 rounded-3xl p-6 max-w-md mx-auto">
        <p className="text-xs text-ink-500 uppercase tracking-wider font-bold mb-3">Your scores</p>
        <p className="text-5xl font-black text-gold-300 mb-2">{avg}%</p>
        <p className="text-xs text-ink-500 mb-4">Average pronunciation accuracy</p>

        <div className="space-y-2 text-left">
          {[
            { label: "Listen & Repeat", key: 1 },
            { label: "Key Words", key: 2 },
            { label: "Your Sentence", key: 3 },
            { label: "Conversation", key: 4 },
          ].map((s) => (
            <div key={s.key} className="flex items-center justify-between text-sm">
              <span className="text-ink-600">{s.label}</span>
              <span className={`font-bold ${scores[s.key] >= 80 ? "text-sage-light" : scores[s.key] >= 50 ? "text-gold-300" : "text-paper"}`}>
                {scores[s.key] !== undefined ? `${scores[s.key]}%` : "—"}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 max-w-md mx-auto">
        <Link to="/dashboard" className="bg-paper-deep border border-cream-300 px-4 py-3 rounded-2xl text-sm font-bold hover:bg-paper-deep transition flex items-center justify-center gap-2">
          <BookOpen className="w-4 h-4" /> Dashboard
        </Link>
        <Link to={`/lesson/${nextDay}`} className="bg-white text-sage-dark px-4 py-3 rounded-2xl text-sm font-black hover:scale-105 transition shadow-lg flex items-center justify-center gap-2">
          Next lesson <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}
